import React, { useRef } from "react";
import {
    intl,
    DmsAltBox,
    DmsPage,
    DmsTable,
    DmsButton,
    DmsButtonGroup,
    getToken,
} from "dmsbasereact";
import { getBaseURL } from "../../build_config";
import { formatDate } from "../../utils/formatters";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { Typography, Chip, Box, IconButton, Tooltip } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SchoolIcon from "@mui/icons-material/School";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadIcon from "@mui/icons-material/Download";

export const MyTrainingsPage = () => {
    const navigate = useNavigate();
    const table = useRef(null);

    const handleDownloadCertificate = async (certificatePk) => {
        try {
            const token = getToken();
            const url = `${getBaseURL()}/certificate/${certificatePk}/my-download`;

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token.access_token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Download failed: ${response.statusText}`);
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `certificate-${certificatePk}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: intl.get("DownloadError").d("Download Error"),
                text: err.message,
            });
        }
    };

    const handleDownloadAttendance = async (filePk, filename) => {
        try {
            const token = getToken();
            const url = `${getBaseURL()}/file/download?pk=${filePk}`;

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token.access_token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Download failed: ${response.statusText}`);
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = filename || "attendance-file";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: intl.get("DownloadError").d("Download Error"),
                text: err.message,
            });
        }
    };


    const getStatusChip = (enrollment_status, completion_date) => {
        if (completion_date) {
            return (
                <Chip
                    label={intl.get("Completed").d("Completed")}
                    color="success"
                    size="small"
                    icon={<CheckCircleIcon />}
                />
            );
        } else if (enrollment_status) {
            return (
                <Chip
                    label={intl.get("InProgress").d("In Progress")}
                    color="primary"
                    size="small"
                />
            );
        } else {
            return (
                <Chip
                    label={intl.get("NotStarted").d("Not Started")}
                    color="default"
                    size="small"
                />
            );
        }
    };

    const getActionButton = (row) => {
        const buttonText = row.enrollment_status
            ? intl.get("View").d("View")
            : intl.get("Start").d("Start");

        return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <DmsButton
                    variant="outlined"
                    size="small"
                    text={buttonText}
                    icon={row.completion_date ? <SchoolIcon /> : <PlayArrowIcon />}
                    onClick={() => navigate(`/training-video/${row.material_pk}`)}
                />
                <Tooltip
                    title={row.training_description || intl.get("NoDescriptionAvailable").d("No description available")}
                    arrow
                    placement="left"
                >
                    <IconButton size="small" color="primary">
                        <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        );
    };

    const header = [
        {
            label: intl.get("TrainingName").d("Training Name"),
            column_name: "training_name",
        },
        {
            label: intl.get("Mandatory").d("Mandatory"),
            column_name: "is_mandatory",
            width: "130px",
            sortable: false,
            element: (row) => row.is_mandatory
                ? <Chip label={intl.get("Mandatory").d("Mandatory")} size="small" variant="outlined" />
                : <Chip label={intl.get("Optional").d("Optional")} size="small" variant="outlined" />,
        },
        {
            label: intl.get("TrainingStatus").d("Status"),
            column_name: "status",
            width: "150px",
            sortable: false,
            element: (row) => getStatusChip(row.enrollment_status, row.completion_date),
        },
        {
            label: intl.get("CompletionDate").d("Completion Date"),
            column_name: "completion_date",
            element: (row) => {
                if (!row.completion_date) return "-";
                return formatDate(row.completion_date);
            },
        },
        {
            label: intl.get("Certificate").d("Certificate"),
            column_name: "certificate",
            width: "280px",
            sortable: false,
            element: (row) => {
                if (!row.certificate_pk) return "-";
                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {intl.get("Code").d("Code")}: {row.certificate_code}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {intl.get("ValidUntil").d("Valid Until")}: {formatDate(row.certificate_expiry)}
                            </Typography>
                        </Box>
                        <DmsButtonGroup>
                            <DmsButton
                                icon={<DownloadIcon />}
                                onClick={() => handleDownloadCertificate(row.certificate_pk)}
                                title={intl.get("DownloadCertificate").d("Download Certificate")}
                            />
                        </DmsButtonGroup>
                    </Box>
                );
            },
        },
        {
            label: intl.get("AttendanceFile").d("Attendance File"),
            column_name: "attendance_file",
            width: "220px",
            sortable: false,
            element: (row) => {
                if (!row.attendance_file_pk) {
                    return (
                        <Typography variant="body2" color="text.secondary">
                            -
                        </Typography>
                    );
                }
                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                maxWidth: "130px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                            }}
                            title={row.attendance_filename}
                        >
                            {row.attendance_filename || intl.get("AttendanceFile").d("Attendance File")}
                        </Typography>
                        <DmsButtonGroup>
                            <DmsButton
                                icon={<DownloadIcon />}
                                onClick={() => handleDownloadAttendance(
                                    row.attendance_file_pk,
                                    row.attendance_filename
                                )}
                                title={intl.get("DownloadFile").d("Download File")}
                            />
                        </DmsButtonGroup>
                    </Box>
                );
            },
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            width: "220px",
            sortable: false,
            element: (row) => getActionButton(row),
        },
    ];

    return (
        <DmsPage name={intl.get("TrainingReports").d("Training Reports")}>
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
                        {intl.get("TrainingReports").d("Training Reports")}
                    </Typography>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <DmsTable
                        header={header}
                        managed={true}
                        url="/training_material/my-trainings/table"
                        ref={table}
                        export_table={false}
                        page_length={10}
                        paging={true}
                    />
                </DmsAltBox.Body>
            </DmsAltBox.Card>
        </DmsPage>
    );
};

export default MyTrainingsPage;
